import {
  type QueryClient,
  type QueryKey,
} from '@tanstack/react-query';
import {
  viewerQueryKeys,
  type PanoramaHotspot,
} from '@ironview/interactive-viewer';

import { IRONVIEW_360_VIEWER_ADAPTER_ID } from './ironview360DataAdapter';

const adapterRoot = [
  'ironview-interactive-viewer',
  IRONVIEW_360_VIEWER_ADAPTER_ID,
] as const;

const isPhotoHotspotKey = (queryKey: QueryKey): boolean =>
  queryKey[0] === adapterRoot[0] &&
  queryKey[1] === adapterRoot[1] &&
  queryKey[3] === 'panorama-photo' &&
  queryKey[5] === 'hotspots';

const isLayoutHotspotKey = (queryKey: QueryKey): boolean =>
  queryKey[0] === adapterRoot[0] &&
  queryKey[1] === adapterRoot[1] &&
  queryKey[3] === 'layout' &&
  queryKey[5] === 'hotspots';

export const getIronview360PhotoHotspotQueries = (
  queryClient: QueryClient,
  projectId: string | number,
): [QueryKey, readonly PanoramaHotspot[] | undefined][] =>
  queryClient.getQueriesData<readonly PanoramaHotspot[]>({
    queryKey: viewerQueryKeys.root(
      IRONVIEW_360_VIEWER_ADAPTER_ID,
      projectId,
    ),
    predicate: (query) => isPhotoHotspotKey(query.queryKey),
  });

export const cancelIronview360PhotoHotspots = (
  queryClient: QueryClient,
  projectId: string | number,
): Promise<void> =>
  queryClient
    .cancelQueries({
      queryKey: viewerQueryKeys.root(
        IRONVIEW_360_VIEWER_ADAPTER_ID,
        projectId,
      ),
      predicate: (query) => isPhotoHotspotKey(query.queryKey),
    })
    .then(() => undefined);

export const setIronview360PhotoHotspotQueries = (
  queryClient: QueryClient,
  projectId: string | number,
  updater: (
    hotspots: readonly PanoramaHotspot[] | undefined,
  ) => readonly PanoramaHotspot[] | undefined,
): void => {
  queryClient.setQueriesData<readonly PanoramaHotspot[]>(
    {
      queryKey: viewerQueryKeys.root(
        IRONVIEW_360_VIEWER_ADAPTER_ID,
        projectId,
      ),
      predicate: (query) => isPhotoHotspotKey(query.queryKey),
    },
    updater,
  );
};

export const invalidateIronview360PhotoHotspots = (
  queryClient: QueryClient,
  photoId?: string | number,
): Promise<void> =>
  queryClient
    .invalidateQueries({
      queryKey: adapterRoot,
      predicate: (query) =>
        isPhotoHotspotKey(query.queryKey) &&
        (photoId == null || String(query.queryKey[4]) === String(photoId)),
    })
    .then(() => undefined);

export const invalidateIronview360LayoutHotspots = (
  queryClient: QueryClient,
  layoutId?: string | number,
): Promise<void> =>
  queryClient
    .invalidateQueries({
      queryKey: adapterRoot,
      predicate: (query) =>
        isLayoutHotspotKey(query.queryKey) &&
        (layoutId == null || String(query.queryKey[4]) === String(layoutId)),
    })
    .then(() => undefined);
